/**
 * Ryan O'Dowd
 * 2022-08-11
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  FlatList,
  TouchableOpacity,
  View,
} from 'react-native';
import React, {
  useEffect,
  useState,
} from 'react';
// @TODO: import ButtonGroup from '@components/common/ButtonGroup';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import PublicGoalPreview from './PublicGoalPreview';
import ScreenWrapper from '@containers/ScreenWrapper';
import Searchbar from '@components/common/Searchbar';
import Tag from './PublicGoalPreview/Tag';
import Text from '@components/common/Text';
import TranslationSelectionDialog from '@components/TranslationSelectionDialog';
import {
  fetchPublicGoals,
} from '@utils/store/actions';
import getStyles from './styles';
import hooks from '@hooks';

const Discover = () => {
  const dispatch = hooks.useTypedDispatch();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => {
    navigation.goBack();
    return true;
  });

  // @TODO: const [_tab, setTab] = useState('featured');
  const [_searchOpen, setSearchOpen] = useState(false);
  const [_searchText, setSearchText] = useState('');

  const [_selectedTagNames, setSelectedTagNames] = useState([]);

  const [_isTranslationMenuOpen, setIsTranslationMenuOpen] = useState(false);

  const isAdmin = hooks.useIsAdmin();

  const backIcon = <Header.BackIcon />;

  // @TODO: abstract into TranslationSelectionDialog {
  const preferredTranslation = hooks.usePreferredTranslation();
  const translations = hooks.useTypedSelector((state) => state.translations.filter((t) => {
    return isAdmin || t.disabled === 0;
  }), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));
  const selectedTranslation = translations.find((t) => t.id === preferredTranslation.id) || translations[0];
  // @TODO: abstract into TranslationSelectionDialog }

  const translationMenu = (// @TODO: make sure this is the same size as the other
    <TouchableOpacity
      style={styles.translationMenuTextWrapper}
      onPress={() => setIsTranslationMenuOpen(true)}
    >
      <Text style={styles.translationMenuText}>{selectedTranslation.abbreviation}</Text>
    </TouchableOpacity>
  );

  const searchItem = ( // @TODO: make search better by filtering on goal_type, for example
    <Header.Icon
      icon='magnify'
      onPress={() => {
        const newSearchOpenState = !_searchOpen;
        if (!newSearchOpenState) {
          setSearchText('');
          setSelectedTagNames([]);
        }
        setSearchOpen(newSearchOpenState);
      }}
    />
  );

  const publicGoals = hooks.useTypedSelector((state) => state.publicGoals);

  const lcSearchText = _searchText ? _searchText.toLocaleLowerCase() : '';
  const searchTerms = lcSearchText ? lcSearchText.split(' ') : [];
  const publicGoalsToDisplay = publicGoals.filter((goal) => {
    // @TODO: console.log(JSON.stringify(goal));
    // @TODO: update this
    if (_selectedTagNames.length && !_selectedTagNames.every((stn) => goal.tags.map((t) => t.name).includes(stn))) {
      // at least one selected tag is not in this goal
      return false;
    }

    return !searchTerms.length || searchTerms.every((searchTerm) => {
      // filter on title
      if (goal.title.toLocaleLowerCase().includes(searchTerm)) {
        return true;
      }

      // filter on description
      if (goal.description?.toLocaleLowerCase().includes(searchTerm)) {
        return true;
      }

      // filter on tags
      if (goal.tags.some((t) => t.name.toLocaleLowerCase().includes(searchTerm))) { // @TODO: make sure this works with public goals
        // at least one selected tag is not in this goal
        return true;
      }

      return false;
    });
  });

  useEffect(() => {
    dispatch(fetchPublicGoals());
  }, [dispatch]);

  return (
    <ScreenWrapper>
      <Header
        leftItems={[backIcon, translationMenu]}
        // @TODO: rightItems={[menuItem]} // @TODO: sort menu (by popularity, difficulty)
        rightItems={[searchItem]}
        title='Discover'
      />
      <FullScreenScrollView>
        <View style={styles.innerContainer}>
          {_searchOpen &&
            <React.Fragment>
              <Searchbar
                onChangeText={(searchText) => setSearchText(searchText)}
                value={_searchText}
                placeholder='Goal name, topic, or tag'
                autoFocus={!_selectedTagNames.length}
              />
              {!!_selectedTagNames.length &&
                <View style={styles.selectedTagsWrapper}>
                  {_selectedTagNames.sort().map((tagName) => {
                    return (
                      <Tag
                        key={tagName}
                        name={tagName}
                        onPress={(t) => setSelectedTagNames(_selectedTagNames.filter((stn) => t !== stn))}
                        isDeletable={true}
                      />
                    );
                  })}
                </View>
              }
            </React.Fragment>
          }
          {/* @TODO: add back when there are more goals
          <ButtonGroup
            containerStyles={styles.statusButtonGroup}
            options={[
              {
                title: 'Featured',
                value: 'featured',
              },
              {
                title: 'Popular',
                value: 'popular',
              },
              {
                title: 'Search',
                value: 'search',
              },
            ]}
            selectedValue={_tab}
            onSelect={(newValue) => setTab(newValue)}
          />
          */}
          <FlatList
            contentContainerStyle={styles.goalsListContainer}
            data={publicGoalsToDisplay}
            renderItem={({item}) => {
              return (
                <PublicGoalPreview
                  id={item.id}
                  title={item.title}
                  description={item.description}
                  type={item.goal_type}
                  tags={item.tags}
                  userCount={item.user_count}
                  // @TODO: probably don't need this anymore publicPassages={item.public_passages}
                  onTagPress={(tag) => {
                    // @TODO: make sure this works
                    setSearchOpen(true);
                    setSelectedTagNames((curr) => [...new Set([...curr, tag])]);
                  }}
                />
              );
            }}
          />
          {/* @TODO: empty screen text if no results for search+tags */}
        </View>
      </FullScreenScrollView>
      <TranslationSelectionDialog
        isOpen={_isTranslationMenuOpen}
        onClose={() => setIsTranslationMenuOpen(false)}
        autoClose={true}
      />
    </ScreenWrapper>
  );
};

export default Discover;
